import { Modal } from "@design-system/Modal";
import { useEffect, useMemo, useState, type FC } from "react";
import { useInactiveQuestionsModalTranslations } from "./i18n";
import { useQuestionnaireOverviewData } from "../../data";
import { useColumns } from "./columns";
import { Table } from "@design-system/Table";
import {
    type RowSelectionState,
    type TableOptions,
} from "@tanstack/react-table";
import { type QuestionnaireOverviewData } from "../../types";
import {
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
} from "@tanstack/react-table";
import { FlexCol } from "@design-system/Layout/Flex";
import { Icon } from "@design-system/Icon";
import { Text } from "@design-system/Typography/Text";
import SearchBar from "@design-system/Inputs/SearchBar";
import { ActionBar } from "./ActionBar";

export const InactiveQuestionsModal: FC<{
    questionnaireId?: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}> = ({ questionnaireId, open, onOpenChange }) => {
    const { t, keys } = useInactiveQuestionsModalTranslations();

    const { questions, isPending: isQuestionsPending } =
        useQuestionnaireOverviewData({
            questionnaireId: questionnaireId,
            inactiveQuestions: true,
        });

    const columns = useColumns();

    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const options: Partial<TableOptions<QuestionnaireOverviewData[number]>> =
        useMemo(
            () => ({
                getPaginationRowModel: getPaginationRowModel(),
                getSortedRowModel: getSortedRowModel(),
                getFilteredRowModel: getFilteredRowModel(),
                onRowSelectionChange: setRowSelection,
                state: {
                    rowSelection,
                    columnVisibility: { questionType: false },
                },
                autoResetPageIndex: false,
            }),
            [rowSelection, setRowSelection],
        );

    const [searchString, setSearchString] = useState<string | undefined>(
        undefined,
    );

    const filteredQuestions = useMemo(() => {
        return questions?.filter(
            (question) =>
                question.name
                    ?.toLowerCase()
                    .includes(searchString?.toLowerCase() ?? "") ||
                question.group?.name
                    ?.toLowerCase()
                    .includes(searchString?.toLowerCase() ?? ""),
        );
    }, [questions, searchString]);

    const selectedQuestionsIds = useMemo(() => {
        return (
            filteredQuestions
                ?.filter((_, idx) => rowSelection[idx])
                ?.map((question) => question.id) ?? []
        );
    }, [questions, rowSelection]);

    const unsetSelection = () => {
        setRowSelection({});
    };

    useEffect(() => {
        setPagination({ ...pagination, pageIndex: 0 });
    }, [setPagination, searchString]);

    return (
        <Modal open={open} onOpenChange={onOpenChange}>
            <Modal.Overlay centerContent>
                <Modal.Content className="w-fit h-full overflow-scroll">
                    <Modal.Header withClose>
                        <Modal.Title>
                            {t(keys.inactive_questions_modal_title)}
                        </Modal.Title>
                        <Modal.Body>
                            <FlexCol gap="5">
                                <SearchBar
                                    searchString={searchString || undefined}
                                    setSearchString={setSearchString}
                                    placeholder={t(
                                        keys.table_search_bar_placeholder,
                                    )}
                                />
                                <Table
                                    columns={columns}
                                    data={filteredQuestions ?? []}
                                    options={options}
                                    loading={isQuestionsPending}
                                    pagination={{
                                        state: pagination,
                                        onPaginationChange: setPagination,
                                        manualPagination: false,
                                    }}
                                    variant="grid"
                                    EmptyBody={EmptyBody}
                                />
                                {questionnaireId && (
                                    <ActionBar
                                        questionnaireId={questionnaireId}
                                        questionsIds={selectedQuestionsIds}
                                        unset={unsetSelection}
                                    />
                                )}
                            </FlexCol>
                        </Modal.Body>
                    </Modal.Header>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

const EmptyBody = () => {
    const { t, keys } = useInactiveQuestionsModalTranslations();

    return (
        <FlexCol
            py="10"
            justifyContent="center"
            alignItems="center"
            w="full"
            className="bg-neutral-25"
        >
            <FlexCol gap="2" alignItems="center" className="my-8">
                <Icon name="search" />
                <FlexCol gap="1" alignItems="center">
                    <Text variant="body-sm-bold">
                        {t(keys.table_empty_state_title)}
                    </Text>
                </FlexCol>
            </FlexCol>
        </FlexCol>
    );
};
